/**
 * Create Disk Image page component.
 *
 * @module pages/CreateDiskImagePage
 */

import { useState, useRef } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Save } from 'lucide-react';
import { PageHeader } from '@/components/layout';
import {
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  Button,
  Input,
  LabelsEditor,
  Select,
  ContentPackageSelect,
} from '@/components/ui';
import type { LabelsEditorRef } from '@/components/ui';
import {
  useCreateDiskImage,
  useCreatePublicDiskImage,
  useBuildDiskImageFromDockerfile,
  useContentPackages,
  useSandboxGroups,
} from '@/hooks';
import type {
  DiskImageRequest,
  DiskImageResponse,
  BuildDiskImageFromDockerfileRequest,
  VmmType,
} from '@/types';

type CreateMode = 'containerImage' | 'dockerfile';

const getVmmOptions = () => [
  { value: 'CloudHypervisor', label: 'Cloud Hypervisor' },
  { value: 'Hcs', label: 'HCS (Windows)' },
];

interface FormState {
  name: string;
  labels: Record<string, string>;
  vmmType: VmmType;
  baseImage: string;
  dockerfile: string;
  buildArgs: Record<string, string>;
  secrets: Record<string, string>;
  contextContentPackageId: string;
  sandboxGroupId: string;
}

const initialFormState: FormState = {
  name: '',
  labels: {},
  vmmType: 'CloudHypervisor',
  baseImage: '',
  dockerfile: '',
  buildArgs: {},
  secrets: {},
  contextContentPackageId: '',
  sandboxGroupId: '',
};

const modeOptions = [
  { value: 'containerImage', label: 'Container Image' },
  { value: 'dockerfile', label: 'Dockerfile' },
];

/**
 * Create disk image page
 */
export function CreateDiskImagePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isPublic = searchParams.get('type') === 'public';
  const preselectedGroupId = searchParams.get('groupId') || '';
  const isGroupLocked = !!preselectedGroupId;
  const createPrivateMutation = useCreateDiskImage();
  const createPublicMutation = useCreatePublicDiskImage();
  const buildFromDockerfileMutation = useBuildDiskImageFromDockerfile();
  const [mode, setMode] = useState<CreateMode>('containerImage');
  const createMutation = isPublic ? createPublicMutation : createPrivateMutation;
  const [form, setForm] = useState<FormState>(() => ({
    ...initialFormState,
    sandboxGroupId: preselectedGroupId,
  }));
  const [errors, setErrors] = useState<Partial<FormState>>({});
  const [showResults, setShowResults] = useState(false);
  const [createdDiskImage, setCreatedDiskImage] = useState<DiskImageResponse | null>(null);
  const [timeTakenMs, setTimeTakenMs] = useState<number>(0);

  // Ref for labels editor to get uncommitted draft
  const labelsEditorRef = useRef<LabelsEditorRef>(null);

  // Content packages for build context — scope follows the form's sandbox group
  const activeGroupId = form.sandboxGroupId || undefined;
  const { data: userContentPackagesData, isLoading: userContentPackagesLoading } =
    useContentPackages(undefined, !activeGroupId);
  const { data: groupContentPackagesData, isLoading: groupContentPackagesLoading } =
    useContentPackages(activeGroupId, !!activeGroupId);
  const contentPackagesData = activeGroupId ? groupContentPackagesData : userContentPackagesData;
  const contentPackagesLoading = activeGroupId
    ? groupContentPackagesLoading
    : userContentPackagesLoading;

  // Sandbox groups for group-scoped disk images
  const { data: sandboxGroups, isLoading: sandboxGroupsLoading } = useSandboxGroups(1, 100);

  const handleChange = (field: keyof FormState, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    // Clear error when user types
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const validate = (): boolean => {
    const newErrors: Partial<FormState> = {};

    if (isPublic && !form.name.trim()) {
      newErrors.name = 'Name is required for public disk images';
    }

    if (mode === 'dockerfile' && !form.name.trim()) {
      newErrors.name = 'Name is required for Dockerfile builds';
    }

    if (mode === 'containerImage' && !form.baseImage.trim()) {
      newErrors.baseImage = 'Base image is required';
    }

    if (mode === 'dockerfile' && !form.dockerfile.trim()) {
      newErrors.dockerfile = 'Dockerfile content is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    // Include any uncommitted draft label from the labels editor
    const finalLabels = { ...form.labels };
    const draft = labelsEditorRef.current?.getDraft();
    if (draft) {
      finalLabels[draft.key] = draft.value;
    }

    const startTime = performance.now();

    if (mode === 'dockerfile') {
      // Dockerfile mode - only supports private disk images
      const request: BuildDiskImageFromDockerfileRequest = {
        name: form.name.trim(),
        vmmType: form.vmmType,
        dockerfile: form.dockerfile.trim(),
        labels: Object.keys(finalLabels).length > 0 ? finalLabels : undefined,
        buildArgs: Object.keys(form.buildArgs).length > 0 ? form.buildArgs : undefined,
        secrets: Object.keys(form.secrets).length > 0 ? form.secrets : undefined,
        contextContentPackageId: form.contextContentPackageId || undefined,
      };

      buildFromDockerfileMutation.mutate(
        { request, sandboxGroupId: form.sandboxGroupId || undefined },
        {
          onSuccess: (data) => {
            const endTime = performance.now();
            setTimeTakenMs(Math.round(endTime - startTime));
            setCreatedDiskImage(data);
            setShowResults(true);
          },
        }
      );
    } else {
      // Container image mode
      const request: DiskImageRequest = {
        ...(isPublic && form.name.trim() ? { name: form.name.trim() } : {}),
        labels: Object.keys(finalLabels).length > 0 ? finalLabels : undefined,
        vmmType: form.vmmType,
        image: {
          base: form.baseImage.trim(),
        },
      };

      const onSuccess = (data: DiskImageResponse) => {
        const endTime = performance.now();
        setTimeTakenMs(Math.round(endTime - startTime));
        setCreatedDiskImage(data);
        setShowResults(true);
      };

      if (isPublic) {
        createPublicMutation.mutate(request, { onSuccess });
      } else {
        createPrivateMutation.mutate(
          { request, sandboxGroupId: form.sandboxGroupId || undefined },
          { onSuccess }
        );
      }
    }
  };

  const handleViewDiskImage = () => {
    if (createdDiskImage) {
      const groupId = form.sandboxGroupId;
      if (groupId) {
        navigate(`/sandbox-groups/${encodeURIComponent(groupId)}/disk-images`);
      } else {
        navigate(`/disk-images/${encodeURIComponent(createdDiskImage.id)}`);
      }
    }
  };

  const handleCreateAnother = () => {
    setShowResults(false);
    setCreatedDiskImage(null);
    setTimeTakenMs(0);
    setForm(initialFormState);
    setMode('containerImage');
  };

  // If showing results, display the results page
  if (showResults && createdDiskImage) {
    return (
      <div>
        <PageHeader
          title={isPublic ? 'Public Disk Image Created' : 'Disk Image Created'}
          description={
            isPublic
              ? 'Your public disk image has been created successfully'
              : 'Your disk image has been created successfully'
          }
          breadcrumbs={[
            { label: 'Dashboard', href: '/dashboard' },
            { label: 'Disk Images', href: '/disk-images' },
            { label: 'Create' },
          ]}
        />

        <div className="space-y-6">
          <Card>
            <CardHeader title="Creation Summary" description="Disk image creation details" />
            <CardBody>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                  <div className="text-sm text-green-700 dark:text-green-400">Status</div>
                  <div className="text-2xl font-bold text-green-700 dark:text-green-300">
                    Success
                  </div>
                </div>
                <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                  <div className="text-sm text-blue-700 dark:text-blue-400">Time Taken</div>
                  <div className="text-2xl font-bold text-blue-700 dark:text-blue-300">
                    {timeTakenMs.toLocaleString()} ms
                  </div>
                </div>
              </div>
            </CardBody>
          </Card>

          <Card>
            <CardHeader
              title="Disk Image Details"
              description="Details of the created disk image"
            />
            <CardBody>
              <div className="space-y-3">
                <div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">ID</div>
                  <div className="font-medium text-gray-900 dark:text-gray-100">
                    {createdDiskImage.id}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">Labels</div>
                  <div className="font-medium text-gray-900 dark:text-gray-100">
                    {createdDiskImage.labels && Object.keys(createdDiskImage.labels).length > 0 ? (
                      <div className="flex flex-wrap gap-2 mt-1">
                        {Object.entries(createdDiskImage.labels as Record<string, string>).map(
                          ([key, value]) => (
                            <span
                              key={key}
                              className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200"
                            >
                              {key}: {value}
                            </span>
                          )
                        )}
                      </div>
                    ) : (
                      'N/A'
                    )}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">Source</div>
                  <div className="font-medium text-gray-900 dark:text-gray-100">
                    {createdDiskImage.image?.base || 'Built from Dockerfile'}
                  </div>
                </div>
              </div>
            </CardBody>
          </Card>

          <Card>
            <CardFooter>
              <div className="flex justify-end gap-3">
                <Button variant="secondary" onClick={handleCreateAnother}>
                  Create Another
                </Button>
                <Button onClick={handleViewDiskImage}>View Disk Image</Button>
              </div>
            </CardFooter>
          </Card>
        </div>
      </div>
    );
  }

  // Normal form view
  return (
    <div>
      <PageHeader
        title={isPublic ? 'Create Public Disk Image' : 'Create Disk Image'}
        description={
          isPublic
            ? 'Create a new public disk image from a container'
            : 'Create a new disk image from a container'
        }
        breadcrumbs={[
          { label: 'Dashboard', href: '/dashboard' },
          { label: 'Disk Images', href: '/disk-images' },
          { label: 'Create' },
        ]}
        action={
          <Link to="/disk-images">
            <Button variant="secondary" leftIcon={<ArrowLeft className="h-4 w-4" />}>
              Cancel
            </Button>
          </Link>
        }
      />

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Basic Information */}
          <Card>
            <CardHeader title="Basic Information" />
            <CardBody className="space-y-4">
              {!isPublic && (
                <Select
                  label="Creation Mode"
                  options={modeOptions}
                  value={mode}
                  onChange={(e) => setMode(e.target.value as CreateMode)}
                />
              )}
              {(isPublic || mode === 'dockerfile') && (
                <Input
                  label="Name"
                  placeholder={isPublic ? 'nginx' : 'my-custom-image'}
                  value={form.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  error={errors.name}
                  helperText={
                    isPublic
                      ? 'Unique name for the public disk image (e.g., nginx, python, redis)'
                      : 'Human-readable name for the disk image (e.g., my-dev-env, python-ml)'
                  }
                  required
                />
              )}
              <LabelsEditor
                ref={labelsEditorRef}
                value={form.labels}
                onChange={(labels) => setForm((prev) => ({ ...prev, labels }))}
                helperText="Add key-value pairs to label this disk image"
              />
              <div>
                <Select
                  label="VMM Type"
                  options={getVmmOptions()}
                  value={form.vmmType}
                  onChange={(e) => handleChange('vmmType', e.target.value as VmmType)}
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Choose the runtime compatibility for the disk image you are creating.
                </p>
              </div>
              {!isPublic && !isGroupLocked && (
                <Select
                  label="Sandbox Group (optional)"
                  options={[
                    { value: '', label: 'None' },
                    ...(sandboxGroups?.map((group) => {
                      const name =
                        group.labels && typeof group.labels === 'object'
                          ? (group.labels as Record<string, string>)['name'] || group.id
                          : group.id;
                      return { value: group.id, label: name };
                    }) || []),
                  ]}
                  value={form.sandboxGroupId}
                  onChange={(e) => handleChange('sandboxGroupId', e.target.value)}
                  disabled={sandboxGroupsLoading}
                  placeholder={sandboxGroupsLoading ? 'Loading groups...' : 'Select a group'}
                />
              )}
            </CardBody>
          </Card>

          {/* Image Configuration */}
          <Card>
            <CardHeader title="Image Configuration" />
            <CardBody className="space-y-4">
              {mode === 'containerImage' ? (
                <Input
                  label="Base Image"
                  placeholder="mcr.microsoft.com/devcontainers/base:ubuntu"
                  value={form.baseImage}
                  onChange={(e) => handleChange('baseImage', e.target.value)}
                  error={errors.baseImage}
                  helperText="The container image to use as base"
                  required
                />
              ) : (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                    Dockerfile
                  </label>
                  <textarea
                    className={`w-full px-4 py-2.5 text-sm border rounded-lg bg-white dark:bg-gray-800 dark:text-white font-mono
                      transition-colors duration-200 focus:outline-none focus:ring-2
                      ${
                        errors.dockerfile
                          ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20'
                          : 'border-gray-300 dark:border-gray-600 focus:border-primary-500 focus:ring-primary-500/20'
                      }`}
                    rows={12}
                    placeholder={`FROM ubuntu:22.04

RUN apt-get update && apt-get install -y \\
    build-essential \\
    curl \\
    git

WORKDIR /workspace`}
                    value={form.dockerfile}
                    onChange={(e) => handleChange('dockerfile', e.target.value)}
                    required
                  />
                  {errors.dockerfile && (
                    <p className="mt-1.5 text-sm text-red-500">{errors.dockerfile}</p>
                  )}
                  <p className="mt-1.5 text-sm text-gray-500 dark:text-gray-400">
                    Enter the Dockerfile content to build the disk image
                  </p>
                </div>
              )}
              {mode === 'dockerfile' && (
                <>
                  <LabelsEditor
                    label="Build Arguments (Optional)"
                    value={form.buildArgs}
                    onChange={(buildArgs) => setForm((prev) => ({ ...prev, buildArgs }))}
                    keyPlaceholder="ARG name"
                    valuePlaceholder="Value"
                    helperText="Build arguments passed via --build-arg. Visible in image history."
                  />
                  <LabelsEditor
                    label="Secrets (Optional)"
                    value={form.secrets}
                    onChange={(secrets) => setForm((prev) => ({ ...prev, secrets }))}
                    keyPlaceholder="Secret ID"
                    valuePlaceholder="Secret value"
                    supportSecrets
                    helperText="Secrets available via RUN --mount=type=secret,id=<key>. Never stored in image layers."
                  />
                  <ContentPackageSelect
                    label="Build Context (Optional)"
                    value={form.contextContentPackageId}
                    onChange={(value) =>
                      setForm((prev) => ({ ...prev, contextContentPackageId: value }))
                    }
                    contentPackages={contentPackagesData}
                    isLoading={contentPackagesLoading}
                    allowNone
                    noneLabel="None (empty context)"
                    helperText="Select a content package to use as the build context. Files from the package will be available to COPY/ADD commands in the Dockerfile. Content package must be in tar.gz format."
                  />
                </>
              )}
            </CardBody>
            <CardFooter>
              <div className="flex justify-end gap-3">
                <Link to="/disk-images">
                  <Button variant="secondary" type="button">
                    Cancel
                  </Button>
                </Link>
                <Button
                  type="submit"
                  leftIcon={<Save className="h-4 w-4" />}
                  isLoading={
                    mode === 'dockerfile'
                      ? buildFromDockerfileMutation.isPending
                      : createMutation.isPending
                  }
                >
                  {mode === 'dockerfile'
                    ? 'Build from Dockerfile'
                    : isPublic
                      ? 'Create Public Disk Image'
                      : 'Create Disk Image'}
                </Button>
              </div>
            </CardFooter>
          </Card>
        </div>
      </form>
    </div>
  );
}
